import { ReactNode } from 'react';
import { getUser } from '../api-services/process';
import useAuth from '../custom-hooks/useAuth';
import Preloader from '../components/preloader/Preloader';

/* main import */
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import { SITELINK } from './Links';

/* main auth process - and - area */
import UserLogin from '../pages/auth/LoginUser';
import GoogleCallback from '../pages/auth/GoogleCallback';
import LocationSetup from '../pages/auth/LocationSetup';
import NotificationSetup from '../pages/auth/NotificationSetup';
import ProfileSetup from '../pages/auth/ProfileSetup';
import RequestResetPassword from '../pages/auth/RequestResetPassword';

/* main app tab view pages */
import Home from '../pages/home/Home';

import VerifyEmail from '../pages/auth/VerifyEmail';
import Messages from '../pages/messaging_old/Messages';
import Community from '../pages/community/Community';
import CommunityDetails from '../pages/community/CommunityDetails';
import Settings from '../pages/settings/Settings';
import CreateCommunity from '../pages/community/CreateCommunity';
import Composer from '../pages/home/Composer';
import CommunityComposer from '../pages/community/CommunityComposer';
import Search from '../pages/home/Search';
import Notifications from '../pages/home/Notifications';
import Subscription from '../pages/subscription/Subscription';
import SavedPost from '../pages/home/SavedPosts';
import LikedPosts from '../pages/home/LikedPosts';
import ViewPost from '../pages/home/ViewPost';
import MoreCommunity from '../pages/community/MoreCommunity';
import MySubscription from '../pages/subscription/MySubscription';
import Messaging from '../pages/messaging/messaging';
import ViewProfile from '../pages/profile/ViewProfile';
import ProfileAccount from '../pages/profile/ProfileAccount';
import { LuBan } from 'react-icons/lu';
import AppLayout from '../layouts/AppLayout';
import MyPosts from '../pages/home/MyPosts';
import CreateReview from '../pages/profile/CreateReview';

// Middleware to validate token
function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading } = useAuth();
  const user = getUser();

  if (loading) return <Preloader loading={loading} />;

  if (isAuthenticated) {
    if (!user?.username || !user?.residency_city_id || !user?.nationality_country_id) {
      return <Navigate to={SITELINK.LOCATION_SETUP} replace />;
    }
  } else {
    localStorage.clear();
  }

  return isAuthenticated ? (
    <>
      <main className="sm:hidden block">{children}</main>
      <div className="sm:flex hidden items-center justify-center min-h-screen bg-gray-100">
        <div className="bg-white p-6 rounded-2xl shadow-lg flex flex-col items-center w-80 text-center">
          <LuBan className="w-12 h-12 text-red-500" />
          <h2 className="text-lg font-semibold mt-4">Catapot Not Available</h2>
          <p className="text-gray-600 mt-2">
            This feature is not available on this device screen. Kindly use a mobile phone.
          </p>
          <Link to="/" className="text-blue-800 text-xs">
            Go Home
          </Link>
        </div>
      </div>
    </>
  ) : (
    <Navigate to={SITELINK.LOGIN} replace />
  );
}

// ProtectedAuthRoute
function ProtectedAuthRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div>
        <Preloader loading={loading} />
      </div>
    );
  }

  return isAuthenticated ? (
    <>
      <main className="sm:hidden block">{children}</main>
      <div className="sm:flex hidden items-center justify-center min-h-screen bg-gray-100">
        <div className="bg-white p-6 rounded-2xl shadow-lg flex flex-col items-center w-80 text-center">
          <LuBan className="w-12 h-12 text-red-500" />
          <h2 className="text-lg font-semibold mt-4">Honneconnect Not Available</h2>
          <p className="text-gray-600 mt-2">
            This feature is not available on this device screen. Kindly use a mobile phone.
          </p>
          <Link to="/" className="text-blue-800 text-xs">
            Go Home
          </Link>
        </div>
      </div>
    </>
  ) : (
    <Navigate to={SITELINK.LOGIN} replace />
  );
}

// config routes
function APPRoutes() {
  return (
    <Router>
      <Routes>
        {/* app landing view */}
        <Route path="/" element={<UserLogin />} />

        {/* main auth process - and - area */}
        <Route path={SITELINK.LOGIN} element={<UserLogin />} />
        <Route path={SITELINK.GOOGLE_LOGIN} element={<GoogleCallback />} />
        <Route path={SITELINK.REQUEST_RESET} element={<RequestResetPassword />} />
        <Route path={SITELINK.LOCATION_SETUP} element={<LocationSetup />} />
        <Route path={SITELINK.AUTH_2FA} element={<VerifyEmail />} />

        <Route
          path={SITELINK.PROFILE_SETUP}
          element={
            <ProtectedAuthRoute>
              <ProfileSetup />
            </ProtectedAuthRoute>
          }
        />
        <Route
          path={SITELINK.NOTIFICATION_SETUP}
          element={
            <ProtectedAuthRoute>
              <NotificationSetup />
            </ProtectedAuthRoute>
          }
        />

        {/* main app tab view pages */}
        <Route
          path={SITELINK.HOME}
          element={
            <ProtectedRoute>
              <AppLayout title="Catapot - Home">
                <Home />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path={`${SITELINK.VIEW_POSTS}/:id`}
          element={
            <ProtectedRoute>
              <ViewPost />
            </ProtectedRoute>
          }
        />

        <Route
          path={SITELINK.COMPOSER}
          element={
            <ProtectedRoute>
              <Composer />
            </ProtectedRoute>
          }
        />

        <Route
          path={`${SITELINK.COM_COMPOSER}/:id`}
          element={
            <ProtectedRoute>
              <CommunityComposer />
            </ProtectedRoute>
          }
        />

        <Route
          path={`${SITELINK.APP_SEARCH}`}
          element={
            <ProtectedRoute>
              <Search />
            </ProtectedRoute>
          }
        />

        <Route
          path={`${SITELINK.APP_SEARCH}/:search`}
          element={
            <ProtectedRoute>
              <Search />
            </ProtectedRoute>
          }
        />

        <Route
          path={`${SITELINK.NOTIFICATIONS}`}
          element={
            <ProtectedRoute>
              <Notifications />
            </ProtectedRoute>
          }
        />

        <Route
          path={`${SITELINK.BOOKMARKS}`}
          element={
            <ProtectedRoute>
              <SavedPost />
            </ProtectedRoute>
          }
        />
        <Route
          path={`${SITELINK.MY_POSTS}`}
          element={
            <ProtectedRoute>
              <MyPosts />
            </ProtectedRoute>
          }
        />

        <Route
          path={`${SITELINK.LIKED_POSTS}`}
          element={
            <ProtectedRoute>
              <LikedPosts />
            </ProtectedRoute>
          }
        />

        <Route
          path={SITELINK.COMMUNITIES}
          element={
            <ProtectedRoute>
              <Community />
            </ProtectedRoute>
          }
        />

        <Route
          path={SITELINK.MORE_COMMUNITIES}
          element={
            <ProtectedRoute>
              <MoreCommunity />
            </ProtectedRoute>
          }
        />

        <Route
          path={SITELINK.NEW_COMMUNITIES}
          element={
            <ProtectedRoute>
              <CreateCommunity />
            </ProtectedRoute>
          }
        />

        <Route
          path={SITELINK.MESSAGES}
          element={
            <ProtectedRoute>
              <Messages />
            </ProtectedRoute>
          }
        />

        <Route
          path={`${SITELINK.VIEW_MESSAGES}/:id`}
          element={
            <ProtectedRoute>
              <Messages />
            </ProtectedRoute>
          }
        />

        <Route
          path={SITELINK.PRIVACY_AND_SETTINGS}
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />

        <Route
          path={SITELINK.SUBSCRIPTIONS}
          element={
            <ProtectedRoute>
              <Subscription />
            </ProtectedRoute>
          }
        />

        <Route
          path={SITELINK.MY_SUBSCRIPTION_HISTORY}
          element={
            <ProtectedRoute>
              <MySubscription />
            </ProtectedRoute>
          }
        />

        <Route
          path={SITELINK.MY_PROFILE}
          element={
            <ProtectedRoute>
              <ProfileAccount />
            </ProtectedRoute>
          }
        />

        <Route
          path={`${SITELINK.MY_PROFILE}/:userID`}
          element={
            <ProtectedRoute>
              <ViewProfile />
            </ProtectedRoute>
          }
        />

        <Route
          path={`${SITELINK.COMMUNITY_ACTIVITIES}/:id`}
          element={
            <ProtectedRoute>
              <AppLayout title="Catapot - Community Details">
                <CommunityDetails />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path={SITELINK.CHAT}
          element={
            <ProtectedRoute>
              <Messaging />
            </ProtectedRoute>
          }
        />

        <Route
          path={`${SITELINK.CHATIN}`}
          element={
            <ProtectedRoute>
              <Messaging />
            </ProtectedRoute>
          }
        />
        <Route
          path={`${SITELINK.MY_PROFILE}/:userID/reviews`}
          element={
            <ProtectedRoute>
              <CreateReview />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
}

export default APPRoutes;
